import React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { IssuePhotos } from "@/components/IssuePhotos";
import { ClaimVideos } from "@/components/ClaimVideos";
import { NoteText } from "@/components/NoteText";
import { ReturnLabelPanel } from "@/components/claims/ReturnLabelPanel";
import { ACTION_META } from "@/components/claims/DecideDialog";
import { toast } from "sonner";

export const CLAIM_TYPE_STYLE = {
  defective: "bg-red-100 text-red-800 border-red-300",
  incompatible: "bg-amber-100 text-amber-800 border-amber-300",
  missing: "bg-sky-100 text-sky-800 border-sky-300",
};
export const STATUS_STYLE = {
  open: "bg-rose-600 text-white border-rose-700",
  warranty: "bg-emerald-600 text-white border-emerald-700",
  reorder: "bg-indigo-600 text-white border-indigo-700",
  refund: "bg-rose-700 text-white border-rose-800",
  new_logo: "bg-amber-500 text-white border-amber-600",
  declined: "bg-slate-600 text-white border-slate-700",
  resolved: "bg-emerald-800 text-white border-emerald-900",
};
const fmtAt = (s) => (s ? s.slice(0, 16).replace("T", " ") : "—");

// Defect note + Solution, both with their EN ⇄ ZH translation.
export function ClaimNotes({ c, lang, t, testid }) {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-rose-700">{t("Defect")} · {t(c.type_label)}</div>
        <NoteText n={{ text: c.note, translation: c.note_translation, translation_lang: c.note_translation_lang }} lang={lang} testid={`${testid}-note`} />
      </div>
      {c.decision_note && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2">
          <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-800">{t("Solution")} · {c.decided_by} · {fmtAt(c.decided_at)}</div>
          <NoteText n={{ text: c.decision_note, translation: c.decision_note_translation, translation_lang: c.decision_note_translation_lang }} lang={lang} testid={`${testid}-solution`} className="text-emerald-950" />
        </div>
      )}
    </div>
  );
}

export function ClaimCard({ c, canDecide, canMailLogo, canSelfResolve, canSendLogo, lang, t, onDecide, onSelfResolve, focused = false }) {
  const qc = useQueryClient();
  const o = c.order || {};
  const logoSent = useMutation({
    mutationFn: () => api.post(`/claims/${c.id}/new-logo-sent`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["claims"] }); qc.invalidateQueries({ queryKey: ["alerts"] }); toast.success(t("Marked: new logo mailed")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <div className={`rounded-lg border bg-white p-4 shadow-sm transition-shadow ${focused ? "border-rose-500 ring-4 ring-rose-300 shadow-lg" : "border-slate-200"}`} id={`claim-${c.id}`} data-testid={`claim-card-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/order/${c.order_id}`} className="font-mono font-bold text-lg hover:underline" data-testid={`claim-po-${c.id}`}>{c.po_number}</Link>
          <Badge className={`border font-bold uppercase tracking-wider ${CLAIM_TYPE_STYLE[c.type] || ""}`} data-testid={`claim-type-${c.id}`}>{t(c.type_label)}</Badge>
          <Badge className={`border font-bold uppercase tracking-wider ${STATUS_STYLE[c.status] || ""}`} data-testid={`claim-status-${c.id}`}>
            {c.status === "open" ? t("OPEN · AFTER-SALE QC") : t(c.decision_label || c.status)}
          </Badge>
          {c.replacement_po && (
            <Link to={`/order/${c.replacement_order_id}`} className="font-mono text-xs px-2 py-0.5 rounded border border-emerald-400 bg-emerald-50 text-emerald-900 hover:bg-emerald-100" data-testid={`claim-replacement-${c.id}`}>
              → {c.replacement_po}{c.scope_label ? ` · ${t(c.scope_label)}` : ""}{c.status === "resolved" ? ` · ✓ ${t("fulfilled")}` : ""}
            </Link>
          )}
          {c.refund_id && (
            <Badge className="border border-rose-300 bg-rose-50 text-rose-800 font-mono text-xs" data-testid={`claim-refund-${c.id}`}>
              💸 ¥{Number(c.refund_amount || 0).toFixed(2)} · {c.refund?.applied ? t("deducted") : c.refund?.status === "approved" ? t("F1 approved") : c.refund?.status === "rejected" ? t("F1 rejected") : t("F1 approval pending")}
              {c.refund?.bill_batch_date ? ` · ${t("bill")} ${c.refund.bill_batch_date}` : ""}
            </Badge>
          )}
          {c.customer_refund_kind === "full" && (() => {
            const sr = c.shopify_refund || {};
            const ok = sr.status === "done" && Number(sr.refunded_amount || 0) > 0;
            return (
              <Badge className={`border text-xs ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-800"}`} data-testid={`claim-shopify-refund-${c.id}`}
                     title={sr.error || (ok ? "" : "Open the order page → Retry Shopify refund")}>
                💳 {ok ? `${t("Shopify refunded")} ${sr.refunded_amount || ""} ${sr.currency || ""}`.trim()
                  : `${t("Shopify refund NOT done")}${sr.error ? ` — ${sr.error.slice(0, 60)}` : sr.status === "done" ? ` — 0 of ${sr.order_total || "?"} refunded` : ""} · ${t("retry on order page")}`}
              </Badge>
            );
          })()}
          {c.customer_refund_kind === "partial" && c.shopify_admin_url && (
            <a href={c.shopify_admin_url} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-0.5 rounded border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100" data-testid={`claim-manual-refund-${c.id}`}>
              ✂ {t("Partial — refund in Shopify")} ↗
            </a>
          )}
          {c.return_requested && <Badge className="border border-sky-300 bg-sky-50 text-sky-800 text-xs" data-testid={`claim-return-badge-${c.id}`}>↩ {t("old set back")}</Badge>}
          {c.status === "new_logo" && (
            <Badge className={`border text-xs ${c.new_logo_sent_at ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid={`claim-logo-state-${c.id}`}>
              {c.new_logo_sent_at ? `🏷 ${t("logo mailed")} ${fmtAt(c.new_logo_sent_at)}` : `🏷 ${t("logo to mail")}`}
            </Badge>
          )}
        </div>
        <div className="text-[11px] font-mono text-slate-500 text-right">
          <div>{t("filed by")} {c.filed_by} · {fmtAt(c.filed_at)}{c.claim_email_sent_at ? ` · ✉ ${t("customer e-mailed")}` : c.claim_email_error ? ` · ✉ ${t("e-mail failed")}` : ""}</div>
          {c.decided_at && <div>{t("decided by")} {c.decided_by} · {fmtAt(c.decided_at)}</div>}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
        <div className="lg:col-span-2 space-y-2">
          <ClaimNotes c={c} lang={lang} t={t} testid={`claim-${c.id}`} />
          <IssuePhotos photos={c.photo_ids || []} size="h-20" testid={`claim-photos-${c.id}`} />
          <ClaimVideos videos={c.videos || []} ids={c.video_ids || []} testid={`claim-videos-${c.id}`} />
        </div>
        <div className="text-xs text-slate-600 space-y-0.5 rounded-md bg-slate-50 border border-slate-200 p-2" data-testid={`claim-order-${c.id}`}>
          <div className="font-semibold text-slate-800">{o.customer_name || "—"}{o.ship_country ? ` · ${o.ship_country}` : ""}</div>
          <div>{[o.year, o.brand, o.model].filter(Boolean).join(" ") || "—"} · {o.color || "—"}{o.logo ? ` · ${o.logo}` : ""}</div>
          <div className="font-mono">F {o.front_cover_code || "—"} / {o.f_clamp_code || "—"} · R {o.rear_cover_code || "—"} / {o.r_clamp_code || "—"}</div>
          <div className="font-mono">CAT {o.catalog_number || "—"} · {t("fulfilled")} {o.fulfilled_at ? o.fulfilled_at.slice(0, 10) : "—"}</div>
        </div>
      </div>
      {c.return_requested && c.status !== "open" && <ReturnLabelPanel claim={c} />}
      {canDecide && c.status === "open" && (
        <div className="mt-3 flex flex-wrap gap-2 justify-end border-t border-slate-100 pt-3">
          {Object.entries(ACTION_META).map(([k, m]) => (
            <Button key={k} data-testid={`claim-${k}-${c.id}`} onClick={() => onDecide(c, k)} className={m.cls}>{t(m.title)}</Button>
          ))}
        </div>
      )}
      {canSelfResolve && c.status === "open" && (
        <div className="mt-2 flex flex-wrap gap-2 justify-end items-center" data-testid={`claim-self-actions-${c.id}`}>
          <span className="text-[11px] text-slate-500 mr-auto">{t("Handled it yourself? Close it without the factory:")}</span>
          {canSendLogo && (
            <Button size="sm" variant="outline" data-testid={`claim-self-new_logo-${c.id}`} onClick={() => onSelfResolve(c, "new_logo")} className="border-amber-500 text-amber-800 hover:bg-amber-50">🏷 {t("New logo sent")}</Button>
          )}
          <Button size="sm" variant="outline" data-testid={`claim-self-resolved-${c.id}`} onClick={() => onSelfResolve(c, "resolved")} className="border-emerald-600 text-emerald-800 hover:bg-emerald-50">✓ {t("Resolved")}</Button>
        </div>
      )}
      {c.self_resolved && c.status !== "open" && (
        <div className="mt-2 text-[11px] text-emerald-800" data-testid={`claim-self-resolved-note-${c.id}`}>✓ {t("Handled by Back Office")} ({c.decided_by}) — {t("no factory alert was sent")}</div>
      )}
      {canMailLogo && c.status === "new_logo" && !c.new_logo_sent_at && (
        <div className="mt-3 flex justify-end border-t border-slate-100 pt-3">
          <Button data-testid={`claim-logo-sent-${c.id}`} onClick={() => logoSent.mutate()} disabled={logoSent.isPending} className="bg-amber-600 hover:bg-amber-700 text-white">🏷 {t("New logo mailed")}</Button>
        </div>
      )}
    </div>
  );
}
